/**
 * Клиентское зеркало канона раздела «Экология» (service-ecology, api-contract §3i). Значения видов,
 * периодичностей, категорий НВОС и видов изменений совпадают с `backend/internal/domain/ecology.go`
 * и CHECK БД дословно. Чистые функции тестируются изолированно (см. ecology.test.ts); валидацию
 * позиции сервер выполняет авторитетно, здесь — те же правила для формы.
 */
import type {
  EcoAnswer,
  EcoAnswers,
  EcoAnswerType,
  EcoCardField,
  EcoCatalogItem,
  EcoCatalogItemInput,
  EcoCategory,
  EcoClarificationType,
  EcoItemSource,
  EcoKind,
  EcoPeriod,
  EcoQuestion,
  EcoQuestionInput,
  EcoRule,
  EcoRuleAction,
  EcoSection,
  EcoSite,
  EcoSiteCategory,
  EcoSiteItem,
  EcoStatusColor,
  EcoTariff,
  EcoUpdateTrigger,
} from '@/lib/api'
import { pluralRu } from '@/lib/orders'

/** Категории объектов НВОС в каноническом порядке; последней — «без категории». */
export const ECO_CATEGORIES: EcoCategory[] = ['I', 'II', 'III', 'IV', 'none']

/** Ярлык категории: «I»…«IV», «Без категории». */
export const ECO_CATEGORY_LABELS: Record<EcoCategory, string> = {
  I: 'I',
  II: 'II',
  III: 'III',
  IV: 'IV',
  none: 'Без категории',
}

/**
 * Варианты категории площадки для выбора кнопками: I–IV, «Без категории» и «Не знаю» (категория не
 * указана) — карточка площадки и поле «Код объекта и категория» карточки документа.
 */
export const ECO_SITE_CATEGORY_OPTIONS: { value: EcoSiteCategory; label: string }[] = [
  ...ECO_CATEGORIES.map((c) => ({ value: c, label: ECO_CATEGORY_LABELS[c] })),
  { value: '', label: 'Не знаю' },
]

/** Короткий ярлык категории для плашек и узких колонок («б/к» — без категории). */
export const ECO_CATEGORY_SHORT_LABELS: Record<EcoCategory, string> = {
  ...ECO_CATEGORY_LABELS,
  none: 'б/к',
}

/**
 * Подсказка о категориях объектов НВОС (макет панели администратора, вкладка «Кому что нужно»);
 * «без категории» — ответ заказчика 17.09: у предприятия без класса свой набор документов.
 */
export const ECO_CATEGORY_HINTS: Record<EcoCategory, string> = {
  I: 'Химические заводы, пищевые производства, маслозаводы, мусорные полигоны',
  II: 'Крупные производственные предприятия, очистные сооружения, сельхозпредприятия',
  III: 'Все предприятия, не относящиеся к I и II',
  IV: 'Офисы без источников выбросов — только отходы',
  none: 'Предприятие не стоит на учёте как объект НВОС — обычно только отходы и отчётность по ним',
}

/** Варианты вида позиции в форме справочника. */
export const ECO_KIND_OPTIONS: { value: EcoKind; label: string }[] = [
  { value: 'document', label: 'Документ / норматив' },
  { value: 'report', label: 'Отчётность / периодическое мероприятие' },
]

/** Короткий ярлык вида позиции в таблице справочника. */
export const ECO_KIND_SHORT_LABELS: Record<EcoKind, string> = {
  document: 'документ',
  report: 'отчётность',
}

/**
 * Подразделы «Экологии» (ecology-lab-control): где показываются строки позиции. Вид строки
 * подраздела — у «Проектных работ» документ, у остальных отчётность (строки устроены одинаково:
 * дата последней сдачи, периодичность).
 */
export const ECO_SECTION_LABELS: Record<EcoSection, string> = {
  projects: 'Проектные работы',
  reports: 'Отчётность',
  lab: 'Лабораторный контроль',
}

/** Вид строк подраздела: «Проектные работы» — документы, остальные — отчётность. */
export function ecoSectionKind(section: EcoSection): EcoKind {
  return section === 'projects' ? 'document' : 'report'
}

/** Подразделы отчётности — варианты для позиции-отчётности в справочнике. */
export const ECO_REPORT_SECTIONS: Exclude<EcoSection, 'projects'>[] = ['reports', 'lab']

/** Подразделы строк раздела (порядок меню). */
export const ECO_SECTIONS: EcoSection[] = ['projects', ...ECO_REPORT_SECTIONS]

/** Значение из адреса (`?section=`) — подраздел или `null`, если такого нет. */
export function parseEcoSection(value: string | null): EcoSection | null {
  return ECO_SECTIONS.find((s) => s === value) ?? null
}

/** Код встроенной позиции «Абонентское обслуживание» (миграция 00037, ecology-subscription). */
export const ECO_SUBSCRIPTION_CODE = 'subs'

/**
 * Группа услуги в мастере заказа (ecology-order-service-search) — пункт меню «Экологии»: подраздел
 * строк позиции, а «Абонентское обслуживание» — своя группа. По данным оно отчётность (строк не
 * ведёт, `section = reports`), но в меню у него отдельная страница, и в «услуги по отчётности»
 * (файл отдела экологии 28.09) оно не входит.
 */
export type EcoServiceGroup = EcoSection | 'subscription'

/** Группы услуг в порядке меню. */
export const ECO_SERVICE_GROUPS: EcoServiceGroup[] = [...ECO_SECTIONS, 'subscription']

export const ECO_SERVICE_GROUP_LABELS: Record<EcoServiceGroup, string> = {
  ...ECO_SECTION_LABELS,
  subscription: 'Абонентское обслуживание',
}

export function ecoServiceGroup(item: Pick<EcoCatalogItem, 'code' | 'section'>): EcoServiceGroup {
  return item.code === ECO_SUBSCRIPTION_CODE ? 'subscription' : item.section
}

/** Текст для поиска услуги: нижний регистр, «ё» как «е», пробелы схлопнуты. */
export function ecoSearchText(s: string): string {
  return s.toLowerCase().replace(/ё/g, 'е').replace(/\s+/g, ' ').trim()
}

/** Услуга подходит под строку поиска — по названию или описанию для клиента; пустая строка — любая. */
export function ecoServiceMatches(
  item: Pick<EcoCatalogItem, 'name' | 'clientDescription'>,
  query: string,
): boolean {
  const q = ecoSearchText(query)
  return q === '' || ecoSearchText(`${item.name} ${item.clientDescription}`).includes(q)
}

/** Ярлыки периодичности отчётности (порядок — порядок выбора в форме). */
export const ECO_PERIOD_LABELS: Record<Exclude<EcoPeriod, ''>, string> = {
  yearly: 'ежегодно',
  quarterly: 'ежеквартально',
  monthly: 'ежемесячно',
  by_pek: 'по программе ПЭК',
  continuous: 'ведётся постоянно',
}

/** Виды изменений в каноническом порядке. */
export const ECO_UPDATE_TRIGGERS: EcoUpdateTrigger[] = [
  'address',
  'legal',
  'equipment',
  'waste',
  'head',
]

/** Полные ярлыки видов изменений — формулировки модалки «Что менялось на предприятии?» макета. */
export const ECO_UPDATE_TRIGGER_LABELS: Record<EcoUpdateTrigger, string> = {
  address: 'Адрес или количество площадок',
  legal: 'Юридическое лицо или наименование',
  equipment: 'Оборудование, технологический процесс, источники выбросов',
  waste: 'Появились новые отходы',
  head: 'Сменился руководитель',
}

/** Короткие ярлыки видов изменений для колонки «Когда обновлять» матрицы. */
export const ECO_UPDATE_TRIGGER_SHORT_LABELS: Record<EcoUpdateTrigger, string> = {
  address: 'адрес или площадки',
  legal: 'юрлицо',
  equipment: 'оборудование, техпроцесс',
  waste: 'новые отходы',
  head: 'смена руководителя',
}

/**
 * Готовые формулировки справочного «Условия» матрицы (макет панели администратора). Поле
 * справочное — кабинет по нему ничего не вычисляет; значение вне списка сохраняется как есть.
 */
export const ECO_CONDITION_PRESETS: string[] = [
  'есть источники выбросов',
  'есть источники выбросов или шума',
  'есть ПГУ',
  'работа с чужими отходами',
  'есть скважина',
  'есть договор ТКО',
]

/** «7 лет», «1 год», «2 года». */
export function ecoYearsLabel(years: number): string {
  return `${years} ${pluralRu(years, 'год', 'года', 'лет')}`
}

/** Дата сдачи «ДД.ММ» (пустая строка, если не задана). */
export function formatEcoDeadline(day: number | null, month: number | null): string {
  if (day === null || month === null) return ''
  return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}`
}

/**
 * Существует ли дата «день.месяц» хотя бы в високосном году (29.02 допустим) — зеркало
 * `domain.ValidEcoDeadline`.
 */
export function isValidEcoDeadline(day: number, month: number): boolean {
  if (!Number.isInteger(day) || !Number.isInteger(month)) return false
  if (month < 1 || month > 12 || day < 1) return false
  // 2024 — високосный: нулевой день следующего месяца = последний день месяца.
  return day <= new Date(2024, month, 0).getDate()
}

/** Разбор даты сдачи «ДД.ММ» (допускается «1.2»); null — пусто или несуществующая дата. */
export function parseEcoDeadline(value: string): { day: number; month: number } | null {
  const m = /^\s*(\d{1,2})\.(\d{1,2})\s*$/.exec(value)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  return isValidEcoDeadline(day, month) ? { day, month } : null
}

/**
 * Срок действия документа или периодичность отчётности одной строкой — колонка справочника и
 * подпись в заказе: «7 лет (с декларацией ДВОС)», «бессрочно при неизменности процессов»,
 * «ежегодно до 25.03», «по программе ПЭК», «договор на год».
 */
export function ecoTermLabel(
  item: Pick<
    EcoCatalogItem,
    'kind' | 'validityYears' | 'validityNote' | 'period' | 'deadlineDay' | 'deadlineMonth'
  >,
): string {
  let base = ''
  if (item.kind === 'document') {
    if (item.validityYears === null) return item.validityNote || 'бессрочно'
    base = ecoYearsLabel(item.validityYears)
  } else if (item.period !== '') {
    const deadline =
      item.period === 'yearly' ? formatEcoDeadline(item.deadlineDay, item.deadlineMonth) : ''
    base = deadline ? `${ECO_PERIOD_LABELS.yearly} до ${deadline}` : ECO_PERIOD_LABELS[item.period]
  }
  if (!base) return item.validityNote || '—'
  return item.validityNote ? `${base} (${item.validityNote})` : base
}

/** Поля позиции для формы справочника. */
export function ecoItemToInput(item: EcoCatalogItem): EcoCatalogItemInput {
  return {
    name: item.name,
    kind: item.kind,
    section: item.section,
    tracked: item.tracked,
    validityYears: item.validityYears,
    validityNote: item.validityNote,
    period: item.period,
    deadlineDay: item.deadlineDay,
    deadlineMonth: item.deadlineMonth,
    developmentTerm: item.developmentTerm,
    approval: item.approval,
    clientDescription: item.clientDescription,
    outdatedBefore: item.outdatedBefore,
    orgWide: item.orgWide,
    categories: item.categories,
    conditionNote: item.conditionNote,
    updateTriggers: item.updateTriggers,
    orderQuestionIds: item.orderQuestionIds,
    dependsOn: item.dependsOn,
    showInOrder: item.showInOrder,
  }
}

/** Значение в каноническом порядке без повторов (категории, виды изменений). */
export function toggleCanonical<T extends string>(
  canon: readonly T[],
  selected: readonly T[],
  value: T,
  on: boolean,
): T[] {
  const set = new Set(selected)
  if (on) set.add(value)
  else set.delete(value)
  return canon.filter((v) => set.has(v))
}

// ---------- Абонентское обслуживание (ecology-subscription) ----------

/** Тарифы сопровождения — как их называет клиенту сервер (макеты 16.09 / 17.09). */
export const ECO_TARIFF_LABELS: Record<EcoTariff, string> = {
  protection: 'Защита Эко',
  prime: 'Прайм Эко',
}

// ---------- Площадки и строки «Проектных работ» / «Отчётности» (ecology-sites) ----------

/** Файлы, которые принимает сервер для сканов и файловых полей строки (files.DocsImagesExt). */
export const ECO_FILE_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png'

/** Предел файла скана и файлового поля строки на сервере — 15 МБ. */
export const ECO_FILE_MAX_BYTES = 15 * 1024 * 1024

/** Предел файлов в поле «несколько файлов» у строки (`domain.EcoFieldFilesMax`). */
export const ECO_FIELD_FILES_MAX = 20

/** Код ФККО цифрами из записи с пробелами и дефисами («4 71 101 01 52 1»); иначе `null`. */
export function normalizeFkkoCode(raw: string): string | null {
  const trimmed = raw.trim()
  if (!/^[\d\s-]*$/.test(trimmed)) return null
  const digits = trimmed.replace(/[\s-]/g, '')
  return digits.length === 11 ? digits : null
}

const HAZARD_CLASSES = ['', 'I', 'II', 'III', 'IV', 'V']

/**
 * Класс опасности вида отхода по коду ФККО — последняя цифра 1–5 («IV»); у группы каталога (код
 * оканчивается нулём) и не кода — `''`. Зеркало `domain.FKKOIsWasteType`.
 */
export function fkkoHazardClass(code: string): string {
  return /^\d{10}[1-5]$/.test(code) ? HAZARD_CLASSES[Number(code[10])] : ''
}

/** Код ФККО по разрядам классификатора: «4 71 101 01 52 1» (не 11 цифр — как есть). */
export function formatFkkoCode(code: string): string {
  const m = /^(\d)(\d{2})(\d{3})(\d{2})(\d{2})(\d)$/.exec(code)
  return m ? m.slice(1).join(' ') : code
}

/** Файловое поле карточки (`file` / `file_multi`): файлы прикрепляются к строке отдельно от формы. */
export function ecoFieldIsFile(type: EcoCardField['type']): boolean {
  return type === 'file' || type === 'file_multi'
}

/** «15.11.2026» из `YYYY-MM-DD` (пусто — пусто). */
export function formatEcoDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso
}

/** Категория площадки для плашки: «НВОС: II категория», «без категории», «категория не указана». */
export function ecoSiteCategoryLabel(category: EcoSiteCategory): string {
  if (category === '') return 'категория не указана'
  if (category === 'none') return 'без категории'
  return `НВОС: ${category} категория`
}

/** Кто указал категорию площадки — подпись под плашкой. */
export function ecoCategorySourceLabel(site: Pick<EcoSite, 'category' | 'categorySource'>): string {
  if (site.category === '') return ''
  return site.categorySource === 'centrika' ? 'указана экологом Центрики' : 'указана вами'
}

/** Подпись происхождения строки: «выполнила Центрика», «из опроса», «внесён вручную». */
export function ecoSourceLabel(source: EcoItemSource): string {
  switch (source) {
    case 'centrika':
      return 'выполнила Центрика'
    case 'survey':
      return 'из опроса'
    default:
      return 'внесён вручную'
  }
}

/** Колонка «Срок действия» документа. */
export function ecoDocumentTermText(item: EcoSiteItem): string {
  if (item.status.key === 'not_required') return 'не требуется для категории площадки'
  if (item.presence === 'needed') return 'документа нет'
  if (item.validUntil) {
    const until = formatEcoDate(item.validUntil)
    return item.status.key === 'overdue' ? `был до ${until}` : `до ${until}`
  }
  if (!item.developedOn && item.validityYears !== null) return 'дата разработки не указана'
  return item.validityNote || 'бессрочно'
}

/** Колонка «Периодичность» отчётности (на площадке). */
export function ecoReportPeriodText(item: EcoSiteItem): string {
  if (item.period === '') return item.validityNote || '—'
  if (item.period === 'yearly') {
    const deadline = formatEcoDeadline(item.deadlineDay, item.deadlineMonth)
    return deadline ? `ежегодно до ${deadline}` : 'ежегодно'
  }
  return ECO_PERIOD_LABELS[item.period]
}

/** Колонка «Ближайший срок» отчётности. */
export function ecoReportDueText(item: EcoSiteItem): string {
  if (item.status.key === 'not_required') return 'не требуется для категории площадки'
  if (item.period === 'continuous') return item.presence === 'needed' ? 'журнал не ведётся' : '—'
  if (item.presence === 'needed') return 'не сдаётся'
  const done = item.section === 'lab' ? 'выполнен' : 'сдан'
  const last = item.lastSubmittedOn ? `${done} ${formatEcoDate(item.lastSubmittedOn)}` : ''
  if (!item.nextDueOn) return last || 'дата сдачи не указана'
  const next = formatEcoDate(item.nextDueOn)
  if (item.status.key === 'overdue')
    return last ? `${last} · срок был до ${next}` : `срок был до ${next}`
  return last ? `${last} · далее до ${next}` : `до ${next}`
}

/** Группа строк одной площадки (в порядке площадок; площадки без строк тоже попадают). */
export interface EcoSiteGroup {
  site: EcoSite
  items: EcoSiteItem[]
}

export function groupEcoItemsBySite(
  sites: EcoSite[],
  items: EcoSiteItem[],
  section: EcoSection,
): EcoSiteGroup[] {
  return sites.map((site) => ({
    site,
    items: items.filter((i) => i.siteId === site.id && i.section === section),
  }))
}

/** Цвета светофора в порядке фильтров. */
export const ECO_STATUS_COLORS: EcoStatusColor[] = ['red', 'amber', 'green', 'gray']

const COLOR_RANK: Record<EcoStatusColor, number> = { red: 0, amber: 1, green: 2, gray: 3 }

/**
 * «Отчётность» и «Лабораторный контроль» — единым списком по всем площадкам (макет варианта Б):
 * сначала красные, затем жёлтые, зелёные и серые; внутри цвета — по ближайшему сроку (без срока — в
 * конце), затем по порядку площадок и названию.
 */
export function sortEcoReports(
  sites: EcoSite[],
  items: EcoSiteItem[],
  section: Exclude<EcoSection, 'projects'> = 'reports',
): EcoSiteItem[] {
  const siteOrder = new Map(sites.map((s, i) => [s.id, i]))
  return items
    .filter((i) => i.section === section && siteOrder.has(i.siteId))
    .sort(
      (a, b) =>
        COLOR_RANK[a.status.color] - COLOR_RANK[b.status.color] ||
        (a.nextDueOn || '9999').localeCompare(b.nextDueOn || '9999') ||
        (siteOrder.get(a.siteId) ?? 0) - (siteOrder.get(b.siteId) ?? 0) ||
        a.name.localeCompare(b.name, 'ru'),
    )
}

/** Строка сводки плитки: цвет и подпись с числом. */
export interface EcoSummaryLine {
  color: EcoStatusColor
  text: string
}

/**
 * Сводка плитки «Проектные работы» / «Отчётность» на обзоре раздела: ненулевые цвета по порядку
 * светофора («1 просрочен», «4 истекает / актуализация», «11 действует»).
 */
export function ecoSummaryLines(
  section: EcoSection,
  counts: Record<EcoStatusColor, number>,
): EcoSummaryLine[] {
  const texts: Record<EcoSection, Record<EcoStatusColor, (n: number) => string>> = {
    projects: {
      red: (n) =>
        `${n} ${pluralRu(n, 'просрочен или отсутствует', 'просрочены или отсутствуют', 'просрочены или отсутствуют')}`,
      amber: (n) =>
        `${n} ${pluralRu(n, 'истекает или требует актуализации', 'истекают или требуют актуализации', 'истекают или требуют актуализации')}`,
      green: (n) => `${n} ${pluralRu(n, 'действует', 'действуют', 'действуют')}`,
      gray: (n) => `${n} не требуется`,
    },
    reports: {
      red: (n) => `${n} просрочено или не сдаётся`,
      amber: (n) => `${n} скоро срок`,
      green: (n) => `${n} сдано или ведётся`,
      gray: (n) => `${n} не требуется`,
    },
    lab: {
      red: (n) => `${n} просрочено или не сдаётся`,
      amber: (n) => `${n} скоро срок`,
      green: (n) => `${n} выполнено`,
      gray: (n) => `${n} не требуется`,
    },
  }
  const text = texts[section]
  return ECO_STATUS_COLORS.filter((c) => counts[c] > 0).map((c) => ({
    color: c,
    text: text[c](counts[c]),
  }))
}

// ---------- Опрос по площадке (ecology-survey) ----------

/** Разделы опроса — шаги мастера после выбора площадки (`section` вопроса — номер с 1). */
export const ECO_SURVEY_SECTIONS = [
  'Учёт и изменения',
  'Деятельность',
  'Документы',
  'Отходы и отчётность',
]

export const ECO_YES = 'Да'
export const ECO_NO = 'Нет'

export const ECO_ANSWER_TYPE_LABELS: Record<EcoAnswerType, string> = {
  yes_no: 'Да / Нет',
  single: 'выбор варианта',
  multi: 'несколько вариантов',
  text: 'текст',
  number: 'число',
}

export const ECO_CLARIFICATION_TYPE_LABELS: Record<EcoClarificationType, string> = {
  date: 'дата',
  number: 'число',
  text: 'текст',
  single: 'выбор варианта',
}

export const ECO_RULE_ACTION_LABELS: Record<EcoRuleAction, string> = {
  need: 'нужен',
  have: 'есть, дата из уточнения',
  update: 'требует актуализации',
  note: 'пометка экологу',
}

/** Поля вопроса для PATCH (всё, кроме id и признаков встроенного вопроса). */
export function ecoQuestionToInput(q: EcoQuestion): EcoQuestionInput {
  return {
    section: q.section,
    text: q.text,
    hint: q.hint,
    answerType: q.answerType,
    options: q.options,
    clarifications: q.clarifications,
    showIfQuestionId: q.showIfQuestionId,
    showIfAnswer: q.showIfAnswer,
    enabled: q.enabled,
  }
}

/** Пустой ответ. */
export function emptyEcoAnswer(): EcoAnswer {
  return { value: '', values: [], clarifications: {} }
}

/** Варианты ответа, на которые ссылаются правила и «показывать, если» (как на сервере). */
export function ecoAnswerValues(q: Pick<EcoQuestion, 'answerType' | 'options'>): string[] {
  if (q.answerType === 'yes_no') return [ECO_YES, ECO_NO]
  if (q.answerType === 'single' || q.answerType === 'multi') return q.options
  return []
}

/** Есть ли ответ на вопрос. */
export function isEcoAnswered(answer: EcoAnswer | undefined): boolean {
  return Boolean(answer && (answer.value.trim() !== '' || answer.values.length > 0))
}

/**
 * Вопрос показывается: включён и выполнено «показывать, если» по цепочке — зеркало
 * `domain.EcoQuestionVisible` (ответ на скрытый вопрос в расчёте не участвует).
 */
export function isEcoQuestionVisible(
  questions: EcoQuestion[],
  answers: EcoAnswers,
  id: string,
): boolean {
  const byId = new Map(questions.map((q) => [q.id, q]))
  let cur = byId.get(id)
  for (let depth = 0; cur && depth <= questions.length; depth++) {
    if (!cur.enabled) return false
    if (!cur.showIfQuestionId) return true
    if (answers[cur.showIfQuestionId]?.value !== cur.showIfAnswer) return false
    cur = byId.get(cur.showIfQuestionId)
  }
  return false
}

/** Видимые вопросы раздела в порядке опроса. */
export function visibleEcoQuestions(
  questions: EcoQuestion[],
  answers: EcoAnswers,
  section: number,
): EcoQuestion[] {
  return questions.filter(
    (q) => q.section === section && isEcoQuestionVisible(questions, answers, q.id),
  )
}

/** Ответ одной строкой для проверки ответов: «Да · Дата разработки: 15.11.2019». */
export function ecoAnswerText(q: EcoQuestion, answer: EcoAnswer | undefined): string {
  if (!answer || !isEcoAnswered(answer)) return ''
  if (q.answerType === 'multi') return answer.values.join(', ')
  if (q.answerType !== 'yes_no' || answer.value !== ECO_YES) return answer.value
  const parts = [answer.value]
  for (const c of q.clarifications) {
    const v = answer.clarifications[c.key]
    if (v)
      parts.push(`${c.label.replace(/[?:]\s*$/, '')}: ${c.type === 'date' ? formatEcoDate(v) : v}`)
  }
  return parts.join(' · ')
}

/** Статус опроса площадки: «пройден 16.09.2025», «начат», «не пройден». */
export function ecoSurveyStatusText(site: Pick<EcoSite, 'survey'>): string {
  if (site.survey.status === 'completed')
    return `пройден ${formatEcoDate(site.survey.completedAt.slice(0, 10))}`
  return site.survey.status === 'draft' ? 'начат' : 'не пройден'
}

/** Условие правила одной строкой для таблицы правил. */
export function ecoRuleConditionText(rule: EcoRule, questions: EcoQuestion[]): string {
  switch (rule.condition) {
    case 'emissions':
      return 'есть источники выбросов'
    case 'categories':
      return `категория ${rule.conditionCategories.map((c) => (c === 'none' ? 'без категории' : c)).join(', ')}`
    case 'answer': {
      const q = questions.find((x) => x.id === rule.conditionQuestionId)
      const clar = q?.clarifications.find((c) => c.key === rule.conditionKey)
      const subject = q
        ? `«${q.text}»${clar ? `, ${clar.label.toLowerCase()}` : ''}`
        : 'удалённый вопрос'
      return `${subject} ${rule.conditionNegate ? '≠' : '='} ${rule.conditionValue}`
    }
    default:
      return 'без условия'
  }
}
